import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { sortBy, merge, find } from "lodash-es";

import {
  APP_USER_ID_KEY,
  CACHE_KEYS,
  PAYMENT_METHODS,
} from "../helpers/constants";
import { getAllUserAccounts, getUser } from "../api/users";
import { getCurrencyDataByCode } from "../helpers/utils";
import userPreferences from "../store/userPreferences";
import useCurrencyFilter from "./useCurrencyFilter";

export default function useCurrentUser() {
  const queryClient = useQueryClient();
  const currentUserId = localStorage?.getItem?.(APP_USER_ID_KEY) || "";
  const { currencyFilter, setCurrencyFilter } = useCurrencyFilter();
  const {
    lastUsedAccount,
    setLastUsedAccount,
    defaultAccount,
    setDefaultAccount,
  } = userPreferences();

  const user = useQuery({
    queryFn: () => getUser({ _id: currentUserId }),
    queryKey: [CACHE_KEYS.CURRENT_USER],
  });

  const accounts = useQuery({
    queryFn: () => getAllUserAccounts({ _id: currentUserId }),
    queryKey: [CACHE_KEYS.CURRENT_USER_ACCOUNTS],
  });

  const getAccountById = (account: any) =>
    find(accounts?.data?.data, { account });
  const accountExist = (account: any) => !!getAccountById(account);

  const availableCurrencies = useMemo(() => {
    const currencies = new Set(
      accounts?.data?.data?.map?.(({ currency }: any) => currency)
    );

    if (currencies.size) {
      const sortedCurrencies = sortBy(
        [...currencies].map((code: any) => getCurrencyDataByCode({ code })),
        ["name"]
      );
      if (!currencies.has(currencyFilter)) {
        setCurrencyFilter({ selectedCurrency: sortedCurrencies[0]?.code });
      }
    } else {
      setCurrencyFilter({ selectedCurrency: "" });
    }

    return [];
  }, [accounts?.data?.data]);

  const resetCache = async () => {
    await Promise.allSettled([
      queryClient.refetchQueries({
        queryKey: [CACHE_KEYS.CURRENT_USER_ACCOUNTS],
      }),
      queryClient.refetchQueries({ queryKey: [CACHE_KEYS.CURRENT_USER] }),
    ]);
  };

  const preselectedAccount = useMemo(
    () =>
      lastUsedAccount ||
      defaultAccount ||
      accounts?.data?.data?.[0]?.account ||
      "",
    [lastUsedAccount, defaultAccount, accounts?.data?.data]
  );
  const enabledAccountsList = useMemo(
    () =>
      accounts?.data?.data?.filter(
        (account: any) => !account?.settings?.disabled
      ) || [],
    [accounts?.data?.data]
  );

  const defaultColors = useMemo(
    () => ({
      transactions: {
        income: {
          bg: {
            r: 80,
            g: 227,
            b: 194,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        expenses: {
          bg: {
            r: 74,
            g: 144,
            b: 226,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
      },
      months: {
        "1": {
          bg: {
            r: 180,
            g: 43,
            b: 43,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "2": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "3": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "4": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "5": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "6": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "7": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "8": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "9": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "10": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "11": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "12": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
      },
      days: {
        "1": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "2": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "3": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "4": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "5": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "6": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        "7": {
          bg: {
            r: 74,
            g: 74,
            b: 74,
            a: 1,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
      },
      balance: {
        positive: {
          bg: {
            r: 0,
            g: 59,
            b: 255,
            a: 0.8,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
        negative: {
          bg: {
            r: 255,
            g: 0,
            b: 6,
            a: 0.61,
          },
          color: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
        },
      },
    }),
    []
  );

  const userColors = user?.data?.data?.data?.settings?.colors || {};

  const mergedColors = merge({}, defaultColors, userColors);

  const allPaymentMethods = useMemo(() => {
    const builtIn = PAYMENT_METHODS();
    const custom = user?.data?.data?.data?.settings?.customPaymentMethods || [];

    return [...builtIn, ...custom];
  }, [user]);

  const getPaymentMethodById = (paymentMethodId: string) => {
    return allPaymentMethods?.find?.(({ id }) => id === paymentMethodId);
  };

  const allEnabledPaymentMethods = useMemo(() => {
    const builtIn =
      user?.data?.data?.data?.settings?.payment_methods?.map?.((id: string) => {
        const data = getPaymentMethodById(id);

        return data;
      }) || [];
    const custom =
      user?.data?.data?.data?.settings?.customPaymentMethods?.filter?.(
        (paymentMethod: any) => paymentMethod?.enabled
      ) || [];

    return [...builtIn, ...custom];
  }, [user]);

  return {
    id: currentUserId,
    userInfo: user?.data?.data?.data,
    accounts,
    availableCurrencies,
    enabledPaymentMethods:
      user?.data?.data?.data?.settings?.payment_methods || [],
    colors: mergedColors,
    voiceAssistants: user?.data?.data?.data?.settings?.voiceAssistants || {
      alexa: { disabled: false },
      google: { disabled: false },
    },
    allCustomPaymentMethods:
      user?.data?.data?.data?.settings?.customPaymentMethods || [],
    getAccountById,
    defaultAccount,
    lastUsedAccount,
    setDefaultAccount,
    setLastUsedAccount,
    preselectedAccount,
    resetCache,
    isLoadingAccounts: !!accounts?.isLoading,
    settings: user?.data?.data?.data?.settings || {},
    accountExist,
    hasRegisteredAccounts: !!accounts?.data?.data?.length,
    enabledAccountsList,
    allUserAccounts: accounts?.data?.data || [],
    allEnabledPaymentMethods,
    allPaymentMethods,
    getPaymentMethodById,
  };
}
